import { useState } from "react";
import { useTranslation } from "react-i18next";

const getTenderServices = (t: (key: string) => string) => [
  { icon: "ri-user-star-line", title: t("tenderService1Title"), desc: t("tenderService1Desc") },
  { icon: "ri-file-search-line", title: t("tenderService2Title"), desc: t("tenderService2Desc") },
  { icon: "ri-secure-payment-line", title: t("tenderService3Title"), desc: t("tenderService3Desc") },
  { icon: "ri-presentation-line", title: t("tenderService4Title"), desc: t("tenderService4Desc") },
  { icon: "ri-megaphone-line", title: t("tenderService5Title"), desc: t("tenderService5Desc") },
  { icon: "ri-phone-find-line", title: t("tenderService6Title"), desc: t("tenderService6Desc") },
];

export default function ServiceTender() {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const tenderServices = getTenderServices(t);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      await fetch("https://readdy.ai/api/form/d7jp0bm0l883cmjgvgv0", { method: "POST", body: data });
      form.reset();
      setSubmitted(true);
    } catch {
      // handle silently
    }
  };

  return (
    <section id="service-5" className="bg-[#0A1628] py-20 lg:py-24">
      <div className="w-full px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 flex items-center justify-center bg-[#0A1628] rounded-xl">
                <i className="ri-file-text-line text-2xl text-white" />
              </div>
              <span className="text-xs font-semibold tracking-widest uppercase text-[#00C2C2]">{t("tenderServiceNum")}</span>
            </div>
            <h2 className="text-4xl font-bold text-white leading-tight">
              {t("tenderTitle")}<br />{t("tenderSubtitle")}
            </h2>
          </div>
          <p className="text-white/60 text-base max-w-md leading-relaxed">
            {t("tenderDesc")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {tenderServices.map((s) => (
            <div key={s.title} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#00C2C2]/40 hover:bg-white/8 transition-all">
              <div className="w-12 h-12 flex items-center justify-center bg-[#0A1628]/15 rounded-xl mb-4">
                <i className={`${s.icon} text-2xl text-[#00C2C2]`} />
              </div>
              <h3 className="text-white font-bold text-base mb-2">{s.title}</h3>
              <p className="text-white/50 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        {!showForm ? (
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => setShowForm(true)}
              className="bg-[#0A1628] text-white text-sm font-bold px-8 py-3.5 rounded-full hover:bg-[#0A1628] transition-all whitespace-nowrap cursor-pointer"
            >
              {t("tenderCta1")}
            </button>
            <a
              href="/tenders"
              className="border border-white/30 text-white text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-all whitespace-nowrap text-center cursor-pointer"
            >
              {t("tenderCta2")}
            </a>
          </div>
        ) : submitted ? (
          <div className="bg-white/10 rounded-2xl p-8 max-w-lg">
            <div className="w-12 h-12 flex items-center justify-center bg-[#0A1628]/20 rounded-xl mb-4">
              <i className="ri-check-line text-2xl text-[#00C2C2]" />
            </div>
            <h3 className="text-white font-bold text-xl mb-2">{t("tenderFormSuccessTitle")}</h3>
            <p className="text-white/60 text-sm">{t("tenderFormSuccessBody")}</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-8 max-w-2xl">
            <h3 className="text-xl font-bold text-[#0A1628] mb-6">{t("tenderFormTitle")}</h3>
            <form
              data-readdy-form
              id="tender-support-form"
              action="https://readdy.ai/api/form/d7jp0bm0l883cmjgvgv0"
              method="POST"
              onSubmit={handleSubmit}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#0A1628]">{t("tenderFormCompany")} *</label>
                <input name="company_name" required className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#00C2C2]" placeholder={t("company")} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#0A1628]">{t("tenderFormEmail")} *</label>
                <input name="email" type="email" required className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#00C2C2]" placeholder="your@email.com" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#0A1628]">{t("tenderFormRef")}</label>
                <input name="tender_reference" className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#00C2C2]" placeholder="e.g. RW/MOH/2024/001" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#0A1628]">{t("tenderFormSector")} *</label>
                <select name="sector" required className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#00C2C2] bg-white">
                  <option value="">{t("selectSector")}</option>
                  {["Health", "Agriculture", "Education", "Infrastructure", "Manufacturing", "ICT", "Other"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1 sm:col-span-2">
                <label className="text-xs font-semibold text-[#0A1628]">{t("tenderFormDetails")}</label>
                <textarea name="details" maxLength={500} className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#00C2C2] resize-none h-20" placeholder="Describe the tender or your requirements..." />
              </div>
              <div className="sm:col-span-2 flex gap-3">
                <button type="submit" className="bg-[#0A1628] text-white text-sm font-bold px-7 py-3 rounded-full hover:bg-[#0A1628] transition-all whitespace-nowrap cursor-pointer">
                  {t("tenderFormSubmit")}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="border border-gray-200 text-gray-600 text-sm font-semibold px-5 py-3 rounded-full hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer">
                  {t("tenderFormCancel")}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </section>
  );
}
